/**
 * Base de datos local (IndexedDB mediante Dexie). Cada entidad se guarda como
 * StoredRecord { id, data, hlc } para que la fusión de la sincronización sea
 * idéntica en cliente y servidor.
 */
import Dexie, { type Table } from 'dexie';
import type { EntityName, FieldWrite } from '@habitos/protocol';

export interface LocalRecord {
  id: string;
  data: Record<string, unknown>;
  hlc: Record<string, string>;
}

export interface OutboxItem {
  seq?: number;
  opId: string;
  entity: EntityName;
  id: string;
  fields: Record<string, FieldWrite>;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

export interface RejectedOp extends OutboxItem {
  code: string;
  detail?: string;
  rejectedAt: number;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export class HabitDB extends Dexie {
  settings!: Table<LocalRecord, string>;
  habits!: Table<LocalRecord, string>;
  entries!: Table<LocalRecord, string>;
  overrides!: Table<LocalRecord, string>;
  groups!: Table<LocalRecord, string>;
  vacations!: Table<LocalRecord, string>;
  reminders!: Table<LocalRecord, string>;
  notes!: Table<LocalRecord, string>;
  listItems!: Table<LocalRecord, string>;
  journal!: Table<LocalRecord, string>;
  focus!: Table<LocalRecord, string>;
  moves!: Table<LocalRecord, string>;
  outbox!: Table<OutboxItem, number>;
  rejected!: Table<RejectedOp, number>;
  meta!: Table<MetaRow, string>;

  constructor(name = 'habitos') {
    super(name);
    this.version(1).stores({
      settings: 'id',
      habits: 'id',
      entries: 'id, data.habitId, data.logicalDate',
      overrides: 'id, data.habitId',
      groups: 'id',
      vacations: 'id',
      reminders: 'id, data.habitId',
      outbox: '++seq, &opId',
      rejected: '++seq, opId',
      meta: 'key',
    });
    // 0.3: notas (cerebro y listas), elementos de lista, diario y sesiones de enfoque. Solo añade tablas:
    // los datos existentes no se tocan.
    this.version(2).stores({
      notes: 'id',
      listItems: 'id, data.listId',
      journal: 'id, data.date',
      focus: 'id, data.logicalDate',
    });
    // 0.6: traslados de ocurrencias (FR-SCH-12). Solo añade una tabla.
    this.version(3).stores({
      moves: 'id, data.habitId',
    });
  }

  table_(entity: EntityName): Table<LocalRecord, string> {
    return this[entity];
  }
}

export let db = new HabitDB();

/** Solo para pruebas: reemplaza la base de datos. */
export function __setDb(next: HabitDB): void {
  db = next;
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return row === undefined ? fallback : (row.value as T);
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}
